import { doc, getDoc, setDoc, updateDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';

const COLLECTIONS = {
  USER_PROGRESS: 'user_progress',
  MODULE_PROGRESS: 'module_progress'
};

// Save step completion to Firebase
export const saveStepCompletion = async (userId, moduleId, stepId, sectionId = null) => {
  try {
    const progressRef = doc(db, COLLECTIONS.USER_PROGRESS, userId);
    const progressDoc = await getDoc(progressRef);
    
    if (progressDoc.exists()) {
      // Update existing progress
      const currentProgress = progressDoc.data();
      const moduleProgress = currentProgress.modules || {};
      const moduleData = moduleProgress[moduleId] || { steps: [], sections: [] };
      
      // Add step if not already completed
      if (!moduleData.steps.includes(stepId)) {
        moduleData.steps.push(stepId);
      }
      
      // If sectionId is provided, add to completed sections
      if (sectionId && !moduleData.sections.includes(sectionId)) {
        moduleData.sections.push(sectionId);
      }
      
      moduleProgress[moduleId] = moduleData;
      
      await updateDoc(progressRef, {
        modules: moduleProgress,
        updatedAt: new Date()
      });
    } else {
      // Create new progress document
      const moduleData = {
        steps: [stepId],
        sections: sectionId ? [sectionId] : []
      };
      
      await setDoc(progressRef, {
        modules: {
          [moduleId]: moduleData
        },
        createdAt: new Date(),
        updatedAt: new Date()
      });
    }
    
    return true;
  } catch (error) {
    console.error('Error saving step completion:', error);
    return false;
  }
};

// Get user's module progress from Firebase
export const getUserModuleProgress = async (userId, moduleId) => {
  try {
    const progressRef = doc(db, COLLECTIONS.USER_PROGRESS, userId);
    const progressDoc = await getDoc(progressRef);
    
    if (progressDoc.exists()) {
      const progress = progressDoc.data();
      const moduleData = progress.modules?.[moduleId];
      if (moduleData) {
        // Return steps array for backward compatibility
        return moduleData.steps || [];
      }
    }
    
    return [];
  } catch (error) {
    console.error('Error getting user module progress:', error);
    return [];
  }
};

// Get user's completed sections for a module
export const getUserModuleSections = async (userId, moduleId) => {
  try {
    const progressRef = doc(db, COLLECTIONS.USER_PROGRESS, userId);
    const progressDoc = await getDoc(progressRef);
    
    if (progressDoc.exists()) {
      const progress = progressDoc.data();
      const moduleData = progress.modules?.[moduleId];
      if (moduleData) {
        return moduleData.sections || [];
      }
    }
    
    return [];
  } catch (error) {
    console.error('Error getting user module sections:', error);
    return [];
  }
};

// Get all user progress
export const getAllUserProgress = async (userId) => {
  try {
    const progressRef = doc(db, COLLECTIONS.USER_PROGRESS, userId);
    const progressDoc = await getDoc(progressRef);
    
    if (progressDoc.exists()) {
      return progressDoc.data();
    }
    
    return { modules: {} };
  } catch (error) {
    console.error('Error getting all user progress:', error);
    return { modules: {} };
  }
};

// Save module completion
export const saveModuleCompletion = async (userId, moduleOrder) => {
  try {
    const progressRef = doc(db, COLLECTIONS.USER_PROGRESS, userId);
    const progressDoc = await getDoc(progressRef);
    
    if (progressDoc.exists()) {
      const currentProgress = progressDoc.data();
      const completedModules = currentProgress.completedModules || [];
      
      if (!completedModules.includes(moduleOrder)) {
        completedModules.push(moduleOrder);
        
        await updateDoc(progressRef, {
          completedModules: completedModules,
          updatedAt: new Date()
        });
      }
    } else {
      await setDoc(progressRef, {
        completedModules: [moduleOrder],
        modules: {},
        createdAt: new Date(),
        updatedAt: new Date()
      });
    }
    
    return true;
  } catch (error) {
    console.error('Error saving module completion:', error);
    return false;
  }
};

// Get completed modules
export const getCompletedModules = async (userId) => {
  try {
    const progressRef = doc(db, COLLECTIONS.USER_PROGRESS, userId);
    const progressDoc = await getDoc(progressRef);
    
    if (progressDoc.exists()) {
      const progress = progressDoc.data();
      return progress.completedModules || [];
    }
    
    return [];
  } catch (error) {
    console.error('Error getting completed modules:', error);
    return [];
  }
};

// Remove step completion from Firebase
export const removeStepCompletion = async (userId, moduleId, stepId) => {
  try {
    const progressRef = doc(db, COLLECTIONS.USER_PROGRESS, userId);
    const progressDoc = await getDoc(progressRef);
    
    if (progressDoc.exists()) {
      const currentProgress = progressDoc.data();
      const moduleProgress = currentProgress.modules || {};
      const moduleData = moduleProgress[moduleId] || { steps: [], sections: [] };
      
      // Remove step from completed steps
      moduleData.steps = moduleData.steps.filter(id => id !== stepId);
      
      // Check if this step removal affects section completion
      // We need to re-evaluate which sections are still completed
      const remainingSteps = moduleData.steps;
      const completedSections = [];
      
      // This would need to be passed from the component with module structure
      // For now, we'll just remove the step and let the component handle section re-evaluation
      
      moduleProgress[moduleId] = moduleData;
      
      await updateDoc(progressRef, {
        modules: moduleProgress,
        updatedAt: new Date()
      });
      
      return true;
    }
    
    return false;
  } catch (error) {
    console.error('Error removing step completion:', error);
    return false;
  }
};
